import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Brain, FileSignature, MessageSquare, BarChart3, Clock, Shield, Users, ChevronRight, Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', franchise_location: '', website: '' });
  const [submittingDemo, setSubmittingDemo] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const startDemo = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmittingDemo(true);
    try {
      await axios.post(`${API}/demo/request`, demoForm);
      navigate('/demo');
    } catch {
      // Still navigate even if notification fails
      navigate('/demo');
    } finally {
      setSubmittingDemo(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] font-[Lato]">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-[#F5F5F5]/85 border-b border-[#E5E5E5]/50 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="spc-logo">
            <div className="w-9 h-9 bg-[#4B2D6E] rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <span className="font-[Outfit] font-bold text-xl text-[#1A1A1A] tracking-tight">SeniorPeerConnect<span className="text-[#E8722A]">™</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#1A1A1A] border border-[#E5E5E5] hover:bg-[#F5F5F5] hover:border-[#4B2D6E] rounded-lg px-6" data-testid="header-login-btn">
                Login
              </Button>
            </Link>
            <Button onClick={() => setDemoDialogOpen(true)} className="bg-[#E8722A] hover:bg-[#d0621f] text-white rounded-lg px-6 shadow-sm" data-testid="header-demo-btn">
              Try Demo
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-0 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-6 items-center">
          {/* Left */}
          <div className="space-y-8 py-8 lg:py-16">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-[#E8722A] font-[Outfit]">
              Purpose-Built for SHS Franchisees
            </p>
            <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[#1A1A1A]">
              The Talent Pipeline Built for Seniors Helping Seniors<span className="text-[#E8722A]">®</span>
            </h1>
            <p className="text-base md:text-lg text-[#595959] leading-relaxed max-w-xl">
              Everything but payroll, on one platform. Screen candidates with AI, send offer letters, schedule interviews, and manage your entire hiring pipeline — all designed specifically for your franchise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => setDemoDialogOpen(true)} className="bg-[#E8722A] hover:bg-[#d0621f] text-white rounded-lg px-8 py-6 text-lg font-medium shadow-sm transition-all duration-300 hover:-translate-y-0.5" data-testid="hero-demo-button">
                Try the Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/login">
                <Button variant="ghost" className="border border-[#E5E5E5] hover:border-[#4B2D6E] rounded-lg px-8 py-6 text-lg font-medium text-[#1A1A1A] transition-all duration-300">
                  Login
                </Button>
              </Link>
            </div>
          </div>

          {/* Right — Large Clean Hero Image */}
          <div className="relative lg:-mr-6">
            <img
              src="/hero-seniors.png"
              alt="An African American senior woman and a Caucasian senior woman laughing together in a sunny garden"
              className="w-full h-[400px] md:h-[520px] lg:h-[600px] object-cover rounded-2xl lg:rounded-r-none lg:rounded-l-2xl shadow-2xl"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 rounded-2xl lg:rounded-r-none lg:rounded-l-2xl bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Social Proof Marquee */}
      <section className="py-12 border-y border-[#E5E5E5] overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            {[1,2,3].map(i => (
              <span key={i} className="font-[Outfit] text-xl md:text-2xl text-[#595959]/40 whitespace-nowrap mx-16">
                Trusted by Seniors Helping Seniors® Franchisees
                <span className="mx-8 text-[#E8722A]">/</span>
                Built for Home Care Recruitment
                <span className="mx-8 text-[#E8722A]">/</span>
                AI-Powered Hiring Pipeline
                <span className="mx-8 text-[#E8722A]">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-[#E8722A] font-[Outfit] mb-4">Everything You Need</p>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A]">One Platform. Zero Headaches.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 md:gap-8">
            {/* Card 1 — AI Screening (Large) */}
            <div className="md:col-span-8 row-span-2 bg-white border border-[#E5E5E5] p-8 md:p-12 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.03)] transition-all duration-300 hover:border-[#4B2D6E]/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#4B2D6E]/10 rounded-xl flex items-center justify-center">
                  <Brain className="h-6 w-6 text-[#4B2D6E]" />
                </div>
                <h3 className="font-[Outfit] text-xl md:text-2xl font-semibold tracking-tight text-[#1A1A1A]">AI Candidate Screening</h3>
              </div>
              <p className="text-[#595959] leading-relaxed mb-8 max-w-lg">
                Every applicant is automatically scored by AI based on their resume, experience, and screening answers. 
                Spend your time on the best candidates, not sorting through stacks of applications.
              </p>
              {/* Mock Dashboard UI */}
              <div className="bg-[#F5F5F5] rounded-xl p-6 border border-[#E5E5E5]">
                <div className="space-y-3">
                  {[
                    { name: 'Sarah Martinez', score: 92, role: 'Companion', stars: 5 },
                    { name: 'James Wilson', score: 85, role: 'CHHA', stars: 4 },
                    { name: 'Maria Lopez', score: 78, role: 'Companion', stars: 4 },
                  ].map((c, i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-[#E5E5E5]">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm text-white ${c.score >= 90 ? 'bg-green-500' : c.score >= 80 ? 'bg-[#E8722A]' : 'bg-[#4B2D6E]'}`}>
                          {c.score}
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-[#1A1A1A]">{c.name}</p>
                          <p className="text-xs text-[#595959]">{c.role} Caregiver</p>
                        </div>
                      </div>
                      <div className="flex gap-0.5">
                        {Array.from({ length: c.stars }).map((_, j) => <Star key={j} className="h-3 w-3 fill-[#E8722A] text-[#E8722A]" />)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card 2 — Offer Letters */}
            <div className="md:col-span-4 bg-white border border-[#E5E5E5] p-8 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.03)] transition-all duration-300 hover:border-[#4B2D6E]/30">
              <div className="w-12 h-12 bg-[#E8722A]/10 rounded-xl flex items-center justify-center mb-4">
                <FileSignature className="h-6 w-6 text-[#E8722A]" />
              </div>
              <h3 className="font-[Outfit] text-xl font-semibold tracking-tight text-[#1A1A1A] mb-2">Offer Letters & E-Signatures</h3>
              <p className="text-[#595959] text-sm leading-relaxed">
                Create multiple templates, preview with live data, and send for electronic signature — all without leaving the platform.
              </p>
            </div>

            {/* Card 3 — Analytics */}
            <div className="md:col-span-4 bg-white border border-[#E5E5E5] p-8 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.03)] transition-all duration-300 hover:border-[#4B2D6E]/30">
              <div className="w-12 h-12 bg-[#4B2D6E]/10 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-[#4B2D6E]" />
              </div>
              <h3 className="font-[Outfit] text-xl font-semibold tracking-tight text-[#1A1A1A] mb-2">Advanced Analytics</h3>
              <p className="text-[#595959] text-sm leading-relaxed">
                Track your pipeline health, time-to-hire, and candidate quality at a glance. Data-driven decisions for your franchise.
              </p>
            </div>

            {/* Card 4 — SMS/Email (Wide) */}
            <div className="md:col-span-8 lg:col-span-12 bg-white border border-[#E5E5E5] p-8 md:p-12 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.03)] transition-all duration-300 hover:border-[#4B2D6E]/30 relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#E8722A]/10 rounded-xl flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-[#E8722A]" />
                  </div>
                  <h3 className="font-[Outfit] text-xl md:text-2xl font-semibold tracking-tight text-[#1A1A1A]">SMS & Email Notifications</h3>
                </div>
                <p className="text-[#595959] leading-relaxed max-w-2xl">
                  Never miss a beat. Candidates get real-time updates via text and email — application confirmations, interview invites, 
                  offer letters — while you get notified the moment something important happens.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  {['New Application', 'Interview Scheduled', 'Offer Sent', 'E-Signature Complete'].map((label) => (
                    <span key={label} className="px-4 py-2 bg-[#4B2D6E]/5 border border-[#4B2D6E]/15 rounded-full text-sm text-[#4B2D6E] font-medium">
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-32 px-6 bg-white border-y border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-[#E8722A] font-[Outfit] mb-4">Get Started in Minutes</p>
            <h2 className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A]">How It Works</h2>
          </div>

          <div className="space-y-16">
            {[
              { num: '01', title: 'Sign Up', desc: 'Create your SeniorPeerConnect account. We\'ll guide you through a simple onboarding checklist to get your franchise configured.' },
              { num: '02', title: 'Set Up Your Territory', desc: 'Add your franchise details, configure interview slots, set up SMS and email notifications, and customize your offer letter templates.' },
              { num: '03', title: 'Start Hiring', desc: 'Post jobs to your branded careers page. Candidates apply, AI screens them automatically, and your pipeline fills itself. Focus on the people, not the paperwork.' },
            ].map((step, i) => (
              <div key={i} className="flex gap-8 items-start">
                <span className="font-[Outfit] text-6xl md:text-7xl font-bold text-[#4B2D6E]/10 leading-none shrink-0">{step.num}</span>
                <div className="pt-2">
                  <h3 className="font-[Outfit] text-xl md:text-2xl font-semibold tracking-tight text-[#1A1A1A] mb-2">{step.title}</h3>
                  <p className="text-[#595959] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra Features Row */}
      <section className="py-20 md:py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Clock, title: 'Save 10+ Hours/Week', desc: 'Automated screening, notifications, and scheduling eliminate manual follow-ups.' },
            { icon: Shield, title: 'Data Protection Built In', desc: 'Automated backups, test data isolation, and role-based access keep your data safe.' },
            { icon: Zap, title: 'Go Live in Under an Hour', desc: 'Guided onboarding walks you through every step. No IT team required.' },
          ].map((item, i) => (
            <div key={i} className="text-center p-8">
              <div className="w-14 h-14 bg-[#4B2D6E]/5 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon className="h-7 w-7 text-[#4B2D6E]" />
              </div>
              <h3 className="font-[Outfit] text-lg font-semibold text-[#1A1A1A] mb-2">{item.title}</h3>
              <p className="text-sm text-[#595959] leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <footer className="bg-[#4B2D6E] py-20 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-[Outfit] text-3xl md:text-5xl font-bold tracking-tight text-white mb-6">Ready to Build Your Team?</h2>
          <p className="text-white/70 text-lg mb-10 max-w-xl mx-auto">
            See how SeniorPeerConnect™ can transform your hiring process. Try the interactive demo — no credit card, no commitment.
          </p>
          <Button onClick={() => setDemoDialogOpen(true)} className="bg-[#E8722A] hover:bg-[#d0621f] text-white rounded-lg px-10 py-6 text-lg font-medium shadow-lg transition-all duration-300 hover:-translate-y-0.5" data-testid="footer-demo-button">
            Try the Demo
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
        <div className="max-w-4xl mx-auto mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">&copy; {new Date().getFullYear()} SeniorPeerConnect™. All rights reserved.</p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy-policy" className="text-white/60 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="text-white/60 hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/login" className="text-white/60 hover:text-white transition-colors">Login</Link>
          </div>
        </div>
      </footer>

      {/* Demo Lead Capture Dialog */}
      <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-2xl tracking-tight">Try SeniorPeerConnect™</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#595959] -mt-2">Enter your info and explore the full platform with sample data. No commitment.</p>
          <form onSubmit={startDemo} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="demo-name">Your Name</Label>
              <Input id="demo-name" value={demoForm.name} onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} placeholder="Jane Smith" required data-testid="demo-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-email">Email</Label>
              <Input id="demo-email" type="email" value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} placeholder="jane@example.com" required data-testid="demo-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-location">Franchise Location (optional)</Label>
              <Input id="demo-location" value={demoForm.franchise_location} onChange={(e) => setDemoForm({ ...demoForm, franchise_location: e.target.value })} placeholder="e.g., Central NJ, Tampa Bay" data-testid="demo-location-input" />
            </div>
            {/* Honeypot — hidden from real users, bots auto-fill it */}
            <input
              type="text"
              name="website"
              value={demoForm.website}
              onChange={(e) => setDemoForm({ ...demoForm, website: e.target.value })}
              autoComplete="off"
              tabIndex={-1}
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
            />
            <Button type="submit" disabled={submittingDemo} className="w-full bg-[#E8722A] hover:bg-[#d0621f] text-white py-5 text-base font-medium rounded-lg" data-testid="demo-submit-btn">
              {submittingDemo ? 'Starting...' : 'Start Demo'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <style>{`
        .marquee-container { overflow: hidden; }
        .marquee-content {
          display: flex;
          animation: marquee 30s linear infinite;
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
